/* ==========================================================================
   #INK JOURNAL — Blog index behaviour
   --------------------------------------------------------------------------
   Progressive enhancement only. With JavaScript disabled the tag pills are
   still ordinary links to Shopify's server-rendered `/tagged/<handle>` URLs
   and every article stays visible.

   Instant client-side filtering is enabled ONLY when the current page holds
   the entire blog (`data-ink-single-page="true"`, i.e. paginate.pages == 1).
   Otherwise filtering would silently hide results living on other pages, so
   the pills fall back to a real navigation.
   ========================================================================== */

const SELECTOR = '[data-ink-blog]';

class InkBlog {
  constructor(root) {
    this.root = root;
    this.singlePage = root.dataset.inkSinglePage === 'true';

    this.pills = Array.from(root.querySelectorAll('[data-ink-filter]'));
    this.cards = Array.from(root.querySelectorAll('[data-ink-card]'));
    this.search = root.querySelector('[data-ink-search-input]');
    this.searchWrap = root.querySelector('[data-ink-search]');
    this.clear = root.querySelector('[data-ink-search-clear]');
    this.empty = root.querySelector('[data-ink-empty]');
    this.reset = root.querySelector('[data-ink-reset]');
    this.status = root.querySelector('[data-ink-status]');
    this.filters = root.querySelector('[data-ink-filters]');
    this.sentinel = root.querySelector('[data-ink-sentinel]');

    this.tag = '*';
    this.query = '';
    this.debounce = 0;

    this.bind();
    this.observeSticky();
    this.restoreFromHash();
  }

  bind() {
    if (this.singlePage) {
      this.pills.forEach((pill) => {
        pill.addEventListener('click', (event) => {
          // Let modified clicks (new tab, download…) behave natively.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          this.setTag(pill.dataset.inkFilter);
        });
      });

      window.addEventListener('hashchange', () => this.restoreFromHash());
    }

    if (this.search) {
      this.search.addEventListener('input', () => {
        window.clearTimeout(this.debounce);
        this.debounce = window.setTimeout(() => {
          this.query = this.search.value.trim().toLowerCase();
          this.searchWrap?.classList.toggle('is-filled', this.query.length > 0);
          this.apply();
        }, 120);
      });

      this.search.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.search.value) {
          event.stopPropagation();
          this.clearSearch();
        }
      });
    }

    this.clear?.addEventListener('click', () => this.clearSearch());
    this.reset?.addEventListener('click', () => {
      this.clearSearch({ silent: true });
      this.setTag('*');
    });
  }

  clearSearch({ silent = false } = {}) {
    if (!this.search) return;
    this.search.value = '';
    this.query = '';
    this.searchWrap?.classList.remove('is-filled');
    if (!silent) this.search.focus();
    this.apply();
  }

  restoreFromHash() {
    if (!this.singlePage) return;
    const match = window.location.hash.match(/tag=([^&]+)/);
    const tag = match ? decodeURIComponent(match[1]) : '*';
    const exists = this.pills.some((pill) => pill.dataset.inkFilter === tag);
    this.setTag(exists ? tag : '*', { pushHash: false });
  }

  setTag(tag, { pushHash = true } = {}) {
    this.tag = tag || '*';

    this.pills.forEach((pill) => {
      const active = pill.dataset.inkFilter === this.tag;
      pill.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (pushHash) {
      // replaceState keeps the back button meaningful (it exits the blog,
      // rather than walking back through every filter the visitor tried).
      const url =
        this.tag === '*'
          ? window.location.pathname + window.location.search
          : `#tag=${encodeURIComponent(this.tag)}`;
      window.history.replaceState(null, '', url);
      this.revealResults();
    }

    this.apply();
  }

  /* Bring the results into view when the visitor is still up at the cover.
     If they are already scrolled down to the grid, leave the scroll alone. */
  revealResults() {
    if (!this.filters) return;
    const { top } = this.filters.getBoundingClientRect();
    if (top <= window.innerHeight * 0.5) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.filters.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  apply() {
    const isDefault = this.tag === '*' && this.query === '';
    let visible = 0;

    this.cards.forEach((card) => {
      const tags = (card.dataset.inkTags || '').split(' ').filter(Boolean);
      const haystack = card.dataset.inkSearch || '';

      const matchesTag = this.tag === '*' || tags.includes(this.tag);
      const matchesQuery = !this.query || haystack.includes(this.query);

      // The cover post is rendered a second time inside the grid so it can be
      // found by a filter. That duplicate stays hidden in the default view.
      const isCoverDuplicate = card.hasAttribute('data-ink-dup');
      const show = matchesTag && matchesQuery && !(isCoverDuplicate && isDefault);

      card.hidden = !show;
      if (show) visible += 1;
    });

    // "Nothing here yet" answers a filter that found nothing. In the default
    // view an empty grid just means the blog is small — don't cry wolf.
    this.empty?.classList.toggle('is-visible', visible === 0 && !isDefault);

    if (this.status) {
      const template = visible === 1 ? this.status.dataset.inkOne : this.status.dataset.inkMany;
      this.status.textContent = (template || '').replace('[count]', String(visible));
    }
  }

  observeSticky() {
    if (!this.filters || !this.sentinel || !('IntersectionObserver' in window)) return;
    if (!this.filters.classList.contains('ink-filters--sticky')) return;

    const observer = new IntersectionObserver(
      ([entry]) => this.filters.classList.toggle('is-stuck', !entry.isIntersecting),
      { threshold: 0, rootMargin: '-1px 0px 0px 0px' }
    );

    observer.observe(this.sentinel);
  }
}

function init(scope = document) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root.inkBlog) return;
    root.inkBlog = new InkBlog(root);
  });
}

init();

// Theme editor: re-initialise when the section is re-rendered.
document.addEventListener('shopify:section:load', (event) => init(event.target));
